import { useMemo } from "react"

import { CACHE_PROFILES } from "@/config/query-cache"
import { transport, useTransportQuery } from "@/transport"
import { useUser as useClerkUser } from "@clerk/nextjs"

import type {
  GetOrganizationUsersResponse,
  OrganizationUserResource,
} from "@/types_api/backend/transport/get_organization_users_response"
import { mapClerkUserId } from "@/utils/auth/user-mappings"

export interface OrganizationUser {
  clerkId: string
  ulid: string | null
  email: string
  displayName: string
  firstName: string | null
  lastName: string | null
  imageUrl: string | null
  createdAt: string | null
  lastUpdatedAt: string | null
}

interface UseOrganizationUsersTransportReturn {
  usersByUlid: Record<string, OrganizationUser>
  currentUser: OrganizationUser | null
  isLoading: boolean
  error: Error | null
}

const EMPTY_USERS_BY_ULID: Record<string, OrganizationUser> = {}

function mapOrganizationUser(user: OrganizationUserResource): OrganizationUser {
  return {
    clerkId: user.clerkId,
    ulid: user.ulid,
    email: user.email,
    displayName: user.displayName,
    firstName: user.firstName ?? null,
    lastName: user.lastName ?? null,
    imageUrl: user.imageUrl ?? null,
    createdAt: user.createdAt ?? null,
    lastUpdatedAt: user.lastUpdatedAt ?? null,
  }
}

function mapUsersByUlid(
  usersByUlid: Record<string, OrganizationUserResource> | undefined,
): Record<string, OrganizationUser> {
  if (!usersByUlid) {
    return EMPTY_USERS_BY_ULID
  }

  return Object.fromEntries(
    Object.entries(usersByUlid).map(([ulid, user]) => [
      ulid,
      mapOrganizationUser(user),
    ]),
  )
}

// Hoisted to a stable module-scope reference so react-query memoizes the
// transformed result. An inline `select` is recreated every render, which
// re-runs the transform and returns a NEW `usersByUlid` object reference each
// time. That referential churn cascades into the Test Sets list controller and
// triggers an unbounded re-render loop (React #185).
const selectUsersByUlid = (
  response: GetOrganizationUsersResponse,
): Record<string, OrganizationUser> => mapUsersByUlid(response.usersByUlid)

export function useOrganizationUsersTransport(): UseOrganizationUsersTransportReturn {
  const { user: clerkUser } = useClerkUser()
  const {
    data: queriedUsersByUlid,
    isLoading,
    error,
  } = useTransportQuery({
    operation: transport.operations.getOrganizationUsers,
    options: {
      ...CACHE_PROFILES.lookup,
      select: selectUsersByUlid,
    },
  })
  const usersByUlid = queriedUsersByUlid ?? EMPTY_USERS_BY_ULID

  const currentUser = useMemo(() => {
    if (!clerkUser?.id) {
      return null
    }

    const activeClerkId = mapClerkUserId(clerkUser.id) ?? clerkUser.id
    return (
      Object.values(usersByUlid).find((u) => u.clerkId === activeClerkId) ??
      null
    )
  }, [clerkUser?.id, usersByUlid])

  return { usersByUlid, currentUser, isLoading, error }
}
